import type { ReactNode } from "react";
import "./Card.css";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";
import { Spinner } from "./Spinner";
import { tx } from "../i18n";

export function Card({
  title,
  meta,
  children,
}: {
  title?: ReactNode;
  meta?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="b-card">
      {title ? (
        <header className="b-card-head">
          <h3 className="b-card-title">{title}</h3>
          {meta ? <div className="card__meta">{meta}</div> : null}
        </header>
      ) : null}
      <div className="card__body">{children}</div>
    </section>
  );
}

/** 빈 상태 — 공통 `.b-empty`. **무엇이 없는지와 다음에 무엇을 하면 되는지**를 함께 적는다 —
 *  「없음」만 적으면 고장인지 비어 있는지 구분이 안 된다. 표·카드·팝오버 안처럼 좁은 자리는 `compact`
 *  (아이콘 없이 제목 한 줄). */
export function EmptyState({
  icon = "inbox",
  title,
  desc,
  action,
  compact,
}: {
  icon?: IconName;
  title: string;
  desc?: string;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={`b-empty${compact ? " is-compact" : ""}`}>
      <Icon name={icon} />
      <p className="b-empty-title">{title}</p>
      {desc ? <p className="b-empty-body">{desc}</p> : null}
      {action}
    </div>
  );
}

/** 알림 줄 — 공통 `.b-alert`. 아이콘 + 말머리 글자 + 본문 — 색은 셋째 신호다. */
export function Alert({
  tone,
  children,
}: {
  tone: "success" | "warning" | "error" | "info";
  children: ReactNode;
}) {
  const head = tx(`ui.alertHead.${tone}`);
  const icon: IconName = { success: "check", warning: "alert", error: "x", info: "info" }[tone] as IconName;
  return (
    <div className={`b-alert b-alert-${tone === "error" ? "danger" : tone}`} role={tone === "error" ? "alert" : "status"}>
      <Icon name={icon} />
      <div className="b-alert-body">
        <p className="b-alert-text">
          <b>{head}</b> · {children}
        </p>
      </div>
    </div>
  );
}

/** 읽는 중. **글자만 두지 않는다** — 「불러오는 중…」 한 줄은 빈 화면과 구분이 안 된다(사람 지적
 *  2026-10-08). 스피너(모양) + 글자 + role=status 가 함께 간다. 빈 상태(EmptyState)·오류(Alert)와
 *  나란히 서는 세 번째 상태 부품이다. */
export function Loading({ text = tx("common.state.loading"), className }: { text?: string; className?: string }) {
  return (
    <p className={["loading", className].filter(Boolean).join(" ")} role="status" aria-busy="true">
      <Spinner size="sm" />
      <span>{text}</span>
    </p>
  );
}
